import { z } from 'zod';

/**
 * Parses a boolean URL search param that may arrive as a boolean or as "true"/"false".
 * Empty or unrecognised values give `defaultValue`.
 */
export function parseBooleanSearchParam<D extends boolean | undefined>(value: unknown, defaultValue: D): boolean | D {
  if (value === true || value === 'true') return true;
  if (value === false || value === 'false') return false;
  return defaultValue;
}

/**
 * Zod field for an optional boolean search param ("true"/"false" strings or booleans);
 * anything else (including absent) parses to `defaultValue`.
 */
export const booleanSearchParam = <D extends boolean | undefined = undefined>(defaultValue?: D) =>
  z.unknown().transform((value) => parseBooleanSearchParam(value, defaultValue as D));

/**
 * Validates a `tab` search param against the allowed tabs.
 * @param legacyMap - Old tab names that still resolve to a current tab (for bookmarked links).
 * @returns The tab, or `defaultTab` when it is missing or unknown.
 */
export function tabSearchParam<T extends string>(
  value: unknown,
  tabs: readonly T[],
  defaultTab: T,
  legacyMap: Record<string, T> = {}
): T {
  if (typeof value !== 'string') return defaultTab;
  if ((tabs as readonly string[]).includes(value)) return value as T;
  return legacyMap[value] ?? defaultTab;
}
